export type PostHogProperties = Record<string, unknown>;

export function logPostHogEvent(
  eventName: string,
  properties?: PostHogProperties
) {
  if (typeof window === 'undefined') return;

  try {
    if (window.localStorage.getItem('ph_upzi_debug') !== 'true') return;

    // eslint-disable-next-line no-console -- Explicitly enabled browser tracking diagnostics.
    console.log('[PostHog event]', eventName, properties ?? {});
  } catch {
    // Storage or console restrictions must not interrupt tracking.
  }
}

export type PostHogConfig = {
  apiKey?: string;
  host?: string;
  appName: string;
};

type TrackerOptions = {
  getConfig: () => PostHogConfig;
  getAnonymousId?: () => string | undefined | Promise<string | undefined>;
  getDistinctId?: () => string | undefined | Promise<string | undefined>;
  fetch?: typeof fetch;
  keepalive?: boolean;
};

function nonEmpty(value: string | undefined): string | undefined {
  return value?.trim() || undefined;
}

/** No SDK, browser globals, host imports, or shared user state at module load. */
export function createPostHogTracker(options: TrackerOptions) {
  async function trackEvent(
    eventName: string,
    properties: PostHogProperties,
    userId = '',
    appId = '',
    userType = 'jobseeker'
  ): Promise<void> {
    try {
      const config = options.getConfig();

      if (!config.apiKey) return;

      const user = nonEmpty(userId);
      const sdkId = user
        ? undefined
        : nonEmpty(await options.getDistinctId?.());
      const anonymousId =
        nonEmpty(appId) ??
        (user || sdkId
          ? undefined
          : nonEmpty(await options.getAnonymousId?.()));
      const distinctId = user ?? sdkId ?? anonymousId;

      // A server request without an identity must not become a shared person.
      if (!distinctId) return;

      const host = (config.host || 'https://us.i.posthog.com').replace(
        /\/+$/,
        ''
      );
      const eventProperties = {
        ...properties,
        distinct_id: distinctId,
        app_name: config.appName,
        app_id: anonymousId ?? '',
        user_id: user ?? '',
        user_type: userType,
        ...(!user ? { $process_person_profile: false } : {}),
      };

      logPostHogEvent(eventName, eventProperties);

      const response = await (options.fetch ?? fetch)(`${host}/capture/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        keepalive: options.keepalive ?? false,
        signal: AbortSignal.timeout(5_000),
        body: JSON.stringify({
          api_key: config.apiKey,
          event: eventName,
          distinct_id: distinctId,
          timestamp: new Date().toISOString(),
          properties: eventProperties,
        }),
      });

      // Release the connection even when the ingest endpoint rejects the event.
      await response.body?.cancel();
    } catch {
      // Analytics must not block the host's navigation or business operation.
    }
  }

  async function trackShortLinkVisit(
    shortCode: string,
    deviceType: string,
    redirectUrl: string,
    userAgent: string,
    ipAddress: string,
    webLink: string,
    mobileWebLink: string | null,
    androidLink: string | null,
    iosLink: string | null,
    utmSource: string,
    utmMedium: string,
    utmCampaign: string,
    utmTerm: string,
    utmContent: string,
    userId: string,
    appId: string
  ): Promise<void> {
    await trackEvent(
      'visit_short_link',
      {
        ip_address: ipAddress,
        user_agent: userAgent,
        short_code: shortCode,
        device_type: deviceType,
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
        utm_term: utmTerm,
        utm_content: utmContent,
        web_link: webLink,
        mobile_web_link: mobileWebLink || '',
        android_link: androidLink || '',
        ios_link: iosLink || '',
        redirect_link: redirectUrl,
        // A link's user_id query parameter may describe its sender/recipient,
        // rather than the visitor. Never identify the visitor from that value.
        link_user_id: userId,
      },
      '',
      appId
    );
  }

  return { trackEvent, trackShortLinkVisit };
}
